'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Topbar from '@/components/layout/Topbar';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const teamMembers = [
{
  name: 'Rahul Sharma',
  role: 'Founder & CEO',
  bio: 'Visionary leader with 15+ years in IT services, AI digital marketing, and digital transformation. Rahul founded BBT Group with a mission to bridge the talent gap between India and global tech companies while delivering AI-powered marketing results.',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
  alt: 'Rahul Sharma, Founder and CEO of BBT Group, professional headshot in business attire'
},
{
  name: 'Priya Mehta',
  role: 'Head of AI Digital Marketing',
  bio: 'AI digital marketing expert with a decade of experience driving growth for Fortune 500 companies and startups. Priya leads our AI-powered digital marketing division with data-driven precision and cutting-edge AI tools.',
  image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face',
  alt: 'Priya Mehta, Head of AI Digital Marketing at BBT Group, professional headshot smiling'
},
{
  name: 'Amit Kumar',
  role: 'Head of IT Staffing — India',
  bio: 'Talent acquisition specialist with deep expertise in the Indian IT market. Amit has successfully placed 1000+ IT professionals including AI/ML engineers, data scientists, and full-stack developers across 200+ companies globally.',
  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
  alt: 'Amit Kumar, Head of IT Staffing India at BBT Group, professional headshot in office setting'
},
{
  name: 'Sarah Johnson',
  role: 'Head of US Operations & Staffing',
  bio: 'Based in the USA, Sarah manages our North American client relationships and IT staffing operations. She ensures seamless delivery of staffing solutions for US-based clients across all technology domains.',
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
  alt: 'Sarah Johnson, Head of US Operations at BBT Group, professional headshot in corporate setting'
}];


const milestones = [
{ year: '2014', event: 'BBT Group founded in India with a focus on IT staffing and training for the Indian tech market' },
{ year: '2016', event: 'Expanded to digital marketing services, helping 50+ businesses grow online with data-driven campaigns' },
{ year: '2018', event: 'Opened US operations office, bridging India-USA talent connections and expanding staffing to the American market' },
{ year: '2020', event: 'Launched Offshore Teams division, enabling companies to build dedicated global teams in India' },
{ year: '2022', event: 'Reached milestone of 1000+ IT professionals placed across India and USA; launched AI/ML talent division' },
{ year: '2024', event: 'Launched AI Digital Marketing division — integrating AI tools into all campaigns for superior performance and ROI' }];


const coreValues = [
{ icon: '🔥', title: 'PASSION', description: 'Innovative and AI-driven thinking is our forte. We are committed to continuously move forward and grow.' },
{ icon: '🤝', title: 'RESPECT', description: 'We value the diversity of talents, perspectives and experiences. We treat our people with respect and equality.' },
{ icon: '💡', title: 'TRANSPARENCY', description: 'We promote a culture of openness that fosters honesty and trust across all our relationships.' },
{ icon: '⚡', title: 'INTEGRITY', description: 'We believe in fostering a work environment where everyone takes ownership and accountability.' },
{ icon: '🎯', title: 'CLIENT FOCUS', description: 'Our customers are the reason for our success. We seek to achieve the highest level of service excellence.' },
{ icon: '📚', title: 'CONSTANT LEARNING', description: 'Pushing ourselves for constant learning and staying ahead of AI and technology trends.' }];


export default function AboutPage() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animated');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <Topbar />
      <Header />
      {/* Page Hero */}
      <section className="page-hero-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white font-poppins leading-tight mb-4">
              Who We Are
            </h1>
            <p className="text-white/70 font-lato text-lg leading-relaxed mb-8">
              BBT Group is an AI-powered digital marketing and talent solutions company helping businesses grow faster through AI Digital Marketing, IT Staffing across India & USA, and Offshore Teams.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="bbt-btn-primary">Get In Touch</Link>
              <Link href="#team" className="bbt-btn-outline border-white/40 text-white hover:bg-white hover:text-primary">Meet Our Team</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <h2 className="bbt-section-title-left">Who we are?</h2>
              <div className="w-12 h-1 bg-primary mb-6"></div>
              <p className="text-text-dark font-poppins text-base leading-relaxed mb-4">
                BBT Group is one of the best AI-powered digital marketing and talent solutions companies in India, with operations in the USA. We help businesses of all sizes acquire customers through AI digital marketing, find top IT talent across India and the USA, and build dedicated global teams.
              </p>
              <p className="text-text-dark font-poppins text-base leading-relaxed mb-4">
                Our core focus is to provide the most efficient, capable, and dynamic workforce for our clients and help them excel globally on any technology platform — powered by AI and data-driven insights.
              </p>
              <p className="text-text-dark font-poppins text-base leading-relaxed mb-4">
                We stand unique with our three-pillar approach — AI Digital Marketing, IT Staffing (India & USA), and Offshore Teams — supported by our AI & Technology Services capability, enabling us to be a true end-to-end digital growth partner.
              </p>
              <p className="text-text-dark font-poppins text-base leading-relaxed">
                We have a proven delivery record of offering high-quality solutions to help our clients achieve their business goals faster and more cost-effectively.
              </p>
            </div>
            <div className="animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=450&fit=crop"
                  alt="BBT Group team members collaborating in a modern office, working on AI digital marketing and technology staffing projects"
                  className="w-full rounded-lg shadow-card-hover" />
                
                <div className="absolute -bottom-6 -right-6 bg-primary text-white p-6 rounded-lg shadow-lg">
                  <div className="text-3xl font-bold font-poppins">500+</div>
                  <div className="text-sm font-lato opacity-90">Happy Clients</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars Architecture */}
      <section className="py-20 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">OUR THREE-PILLAR ARCHITECTURE</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">AI Digital Marketing. India & USA Staffing. Offshore Teams. One unified growth partner.</p>
          </div>

          {/* Architecture Diagram */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-block bg-footer-bg text-white px-10 py-5 rounded-xl shadow-lg">
                <div className="text-2xl font-bold font-poppins">BBT</div>
                <div className="text-primary text-sm font-lato mt-1">AI Digital Marketing & Staffing Partner</div>
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center mb-4">
              <div className="w-px h-8 bg-gray-300"></div>
            </div>

            {/* Three Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
              {[
              {
                title: 'AI DIGITAL MARKETING',
                desc: 'AI-powered campaigns to acquire customers and grow online',
                href: '/digital-marketing',
                color: 'border-blue-500',
                bg: 'bg-blue-50',
                textColor: 'text-blue-700',
                flag: '🤖'
              },
              {
                title: 'IT STAFFING',
                desc: 'Top IT talent in India & USA — AI/ML, dev, cloud, and more',
                href: '/it-staffing',
                color: 'border-indigo-500',
                bg: 'bg-indigo-50',
                textColor: 'text-indigo-700',
                flag: '🇮🇳🇺🇸'
              },
              {
                title: 'OFFSHORE TEAMS',
                desc: 'Dedicated global teams in India for your business',
                href: '/offshore-teams',
                color: 'border-cyan-500',
                bg: 'bg-cyan-50',
                textColor: 'text-cyan-700',
                flag: '🌍'
              }]?.map((pillar, i) =>
              <Link key={i} href={pillar?.href}>
                  <div className={`${pillar?.bg} border-t-4 ${pillar?.color} rounded-lg p-6 text-center hover:shadow-card-hover hover:-translate-y-1 transition-all`}>
                    <div className="text-2xl mb-2">{pillar?.flag}</div>
                    <h4 className={`font-bold font-lato text-sm uppercase tracking-wide mb-3 ${pillar?.textColor}`}>{pillar?.title}</h4>
                    <p className="text-gray-600 text-sm font-poppins">{pillar?.desc}</p>
                  </div>
                </Link>
              )}
            </div>

            {/* Connector to Technology */}
            <div className="flex justify-center mb-4">
              <div className="w-px h-8 bg-gray-300"></div>
            </div>

            {/* Technology Services */}
            <div className="text-center">
              <Link href="/offshore-teams#technology">
                <div className="inline-block bg-white border-2 border-primary rounded-xl px-10 py-5 hover:bg-primary hover:text-white transition-all group shadow-card">
                  <div className="text-xs text-gray-400 group-hover:text-white/70 font-lato uppercase tracking-widest mb-1">Supporting Capability</div>
                  <div className="font-bold text-primary group-hover:text-white font-lato text-base uppercase tracking-wide">AI & TECHNOLOGY SERVICES</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="bbt-section-title-left">Our Core Values</h2>
          <div className="w-12 h-1 bg-primary mb-10"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues?.map((value, i) =>
            <div key={i} className="bbt-value-card animate-on-scroll" style={{ animationDelay: `${i % 3 * 0.1}s` }}>
                <div className="text-3xl mb-3">{value?.icon}</div>
                <h4 className="font-bold text-text-main font-lato text-sm uppercase tracking-wide mb-3">{value?.title}</h4>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed">{value?.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-secondary-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="animate-on-scroll">
              <h2 className="bbt-section-title-left">Our Mission</h2>
              <div className="w-10 h-1 bg-primary mb-5"></div>
              <p className="text-text-dark font-poppins text-base leading-relaxed">
                To become the market leader in AI-powered digital marketing and talent solutions — providing the most cost-effective AI Digital Marketing, IT Staffing (India & USA), and Offshore Team services for our customers by applying disruptive AI technology and enabling businesses to build cutting-edge capabilities for tomorrow.
              </p>
            </div>
            <div className="animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <h2 className="bbt-section-title-left">Our Vision</h2>
              <div className="w-10 h-1 bg-primary mb-5"></div>
              <p className="text-text-dark font-poppins text-base leading-relaxed">
                To be the best AI-driven digital marketing and talent partner of choice for forward-looking businesses — transforming ideas into reality by leveraging AI digital marketing, skilled IT talent across India and the USA, and dedicated offshore teams to create business advantage and becoming the largest integrated AI digital growth company in India and the USA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section id="team" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="bbt-section-title">OUR LEADERSHIP TEAM</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-3 mb-4"></div>
            <p className="bbt-section-subtitle">Experienced leaders driving BBT Group&apos;s mission in AI digital marketing and India & USA staffing</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers?.map((member, i) =>
            <div key={i} className="text-center animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="relative mb-4 inline-block">
                  <img
                  src={member?.image}
                  alt={member?.alt}
                  className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-primary/20 hover:border-primary transition-all" />
                
                </div>
                <h4 className="font-bold text-text-main font-lato text-base mb-1">{member?.name}</h4>
                <p className="text-primary text-sm font-lato font-semibold mb-3">{member?.role}</p>
                <p className="text-gray-600 text-sm font-poppins leading-relaxed">{member?.bio}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20" style={{ backgroundColor: '#6d97bf' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white font-poppins mb-3">OUR JOURNEY</h2>
            <div className="w-16 h-1 bg-white/50 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones?.map((milestone, i) =>
            <div key={i} className="bg-white/10 border border-white/20 rounded-lg p-6 animate-on-scroll" style={{ animationDelay: `${i % 3 * 0.1}s` }}>
                <div className="text-3xl font-bold text-primary font-poppins mb-3">{milestone?.year}</div>
                <p className="text-white/80 text-sm font-poppins leading-relaxed">{milestone?.event}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bbt-cta-section rounded-lg">
            <h2 className="text-2xl font-bold text-text-main font-poppins mb-3 uppercase tracking-wide">READY TO GROW WITH AI DIGITAL MARKETING & TOP IT TALENT?</h2>
            <p className="text-gray-600 font-poppins mb-8 text-lg">&ldquo;Drop us a line and we&apos;ll get back to you right away&rdquo;</p>
            <Link href="/contact" className="bbt-btn-primary">GET CONNECTED WITH US</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>);

}